import React, { useEffect, useMemo, useState } from "react";
import api, { dkk, num, formatErr } from "../lib/api";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { SlidersHorizontal, ChevronRight, UploadCloud, FileSpreadsheet, CheckCircle2, X, TrendingUp, Banknote, Tag, Gauge } from "lucide-react";
import { toast } from "sonner";
import RevenueThresholdsDialog from "../components/RevenueThresholdsDialog";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

const VAT_RATE = 0.25;
const FORECAST_BUFFER = 500;

const fromGross = (gross, withVat) => withVat ? gross : gross / (1 + VAT_RATE);

const fmt = (v) => {
  if (v === 0) return "—";
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(v));
};
const fmtSigned = (v) => {
  if (!v) return "—";
  const r = Math.round(v);
  return (r > 0 ? "+" : "") + new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(r);
};

export default function Revenue() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [withVat, setWithVat] = useState(true);
  const [subtractCosts, setSubtractCosts] = useState(false);
  const [withForecast, setWithForecast] = useState(false);
  const [withSales, setWithSales] = useState(true);
  const [withCompanyFixed, setWithCompanyFixed] = useState(true);
  const [withCompanyVariable, setWithCompanyVariable] = useState(true);
  const [settings, setSettings] = useState(null);
  const [allCars, setAllCars] = useState([]);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [soldExpanded, setSoldExpanded] = useState(false);
  const [gomoreExpanded, setGomoreExpanded] = useState(false);
  const [carbuddiExpanded, setCarbuddiExpanded] = useState(false);
  const [carbuddiEdit, setCarbuddiEdit] = useState(null);

  const load = (yr) => {
    setLoading(true);
    Promise.all([
      api.get("/revenue/monthly", { params: { year: yr } }),
      api.get("/settings"),
      api.get("/cars"),
      api.get("/dashboard/stats"),
    ])
      .then(([r, s, c, st]) => {
        setData(r.data);
        setSettings(s.data);
        setAllCars(c.data);
        setStats(st.data);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(year); }, [year]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Forventet indtjening pr. bil (baseret på bilens egen historik) — undgår at
  // misvisende snit pålægges biler der reelt tjener mere/mindre end resten.
  const expectedByPlate = useMemo(() => {
    if (!data?.rows) return {};
    const map = {};
    data.rows.forEach((r) => {
      // Brug bilens egne udfyldte måneder i året (gross fra GoMore)
      const own = (r.actual_months || []).filter((v) => v > 0);
      // Fald tilbage til evt. manuelt udfyldt expected_revenue
      const expectedOwn = (r.expected_months || []).filter((v) => v > 0);
      let avg = 0;
      if (own.length >= 1) {
        avg = own.reduce((a, v) => a + v, 0) / own.length;
      } else if (expectedOwn.length >= 1) {
        avg = expectedOwn.reduce((a, v) => a + v, 0) / expectedOwn.length;
      }
      map[r.license_plate] = Math.max(0, avg - FORECAST_BUFFER);
    });
    return map;
  }, [data]);

  // Backwards-kompatibel global snit (vises kun som sub-tekst på toggle)
  const expectedPerMonth = useMemo(() => {
    if (!data?.rows) return 0;
    const cells = data.rows
      .filter((r) => r.status === "active")
      .flatMap((r) => r.actual_months.filter((v) => v > 0));
    if (cells.length === 0) return 0;
    const avg = cells.reduce((a, v) => a + v, 0) / cells.length;
    return Math.max(0, avg - FORECAST_BUFFER);
  }, [data]);

  // GoMore-rækker: KUN aktive GoMore-biler.
  // Carbuddi-biler med historisk GoMore-indtjening flyttes til soldRental-sektionen
  // ("Solgte biler · historik") så de ikke tæller med i den aktive GoMore-flåde.
  // Lager-biler med historisk GoMore-indtjening bliver i rows (de var i drift før).
  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const eligible = data.rows.filter((r) => {
      if (r.status === "sold") return false;
      // Aktiv GoMore-bil
      if (r.status === "active" && (r.platform || "gomore") !== "carbuddi") return true;
      // Lager-bil med historisk GoMore-indtjening (carbuddi-biler ekskluderes
      // og vises i stedet i "Solgte biler · historik" — se soldRental).
      if (r.status === "lager") {
        const hasGomoreEarnings = (r.actual_months || []).some((v) => v > 0);
        return hasGomoreEarnings;
      }
      return false;
    });
    const arr = eligible.map((r) => {
      const fixedEx = r.monthly_fixed_cost || 0;
      const fixedDisplayed = withVat ? fixedEx * (1 + VAT_RATE) : fixedEx;
      const proratedEx = r.monthly_fixed_cost_per_month || Array(12).fill(fixedEx);

      // Hvor begynder bilen at være "i udlejning" i året? Backend leverer
      // first_month_index (0..11 eller null) baseret på data → tidligere år →
      // purchase_date. Falder tilbage til ældre signaler hvis feltet mangler.
      const backendFirst = (typeof r.first_month_index === "number") ? r.first_month_index : -1;
      const firstActualIdx = r.actual_months.findIndex((v) => v > 0);
      const hadEarlierData = !!r.has_earlier_data;
      const firstAvailableIdx = backendFirst >= 0
        ? backendFirst
        : (hadEarlierData ? 0 : firstActualIdx);
      const carInService = firstAvailableIdx >= 0;

      // Sidste måned med forventet aktiv udlejning. Backend sætter last_month_index
      // til måneden FØR scheduled_lager_date — derefter er bilen på lager og må
      // ikke vise forecast (kun faktisk data fra perioden bevares).
      const lastAllowedIdx = (typeof r.last_month_index === "number") ? r.last_month_index : 11;

      // Forventet pr. måned: bilens egne snit (hvis data); ellers flådesnit
      const ownExpected = expectedByPlate[r.license_plate] || 0;
      const expectedForCar = ownExpected > 0 ? ownExpected : expectedPerMonth;

      const cells = MONTHS.map((_, i) => {
        const actualGross = r.actual_months[i] || 0;
        const allowExpected = carInService && i >= firstAvailableIdx && i <= lastAllowedIdx;
        const usingExpected = actualGross === 0 && withForecast && expectedForCar > 0 && allowExpected;
        const baseGross = actualGross > 0 ? actualGross : (usingExpected ? expectedForCar : 0);
        const isEmpty = baseGross === 0;

        const baseDisplayed = fromGross(baseGross, withVat);
        const baseEx = fromGross(baseGross, false);
        // Daglig allokering: brug prorated for denne måned i stedet for fuld månedlig
        const proratedExMonth = proratedEx[i] || 0;
        const proratedDisplayed = withVat ? proratedExMonth * (1 + VAT_RATE) : proratedExMonth;
        const fixedSubtract = (baseGross > 0 && subtractCosts) ? proratedExMonth : 0;
        const fixedSubtractDisplayed = (baseGross > 0 && subtractCosts) ? proratedDisplayed : 0;

        const netEx = baseEx - fixedSubtract;
        const netDisplayed = withVat ? baseDisplayed - fixedSubtractDisplayed : baseEx - fixedSubtract;

        return {
          actualGross, usingExpected, baseGross,
          baseDisplayed, baseEx, netEx, netDisplayed,
          empty: isEmpty,
        };
      });
      const totalDisplayed = cells.reduce((a, c) => a + c.netDisplayed, 0);
      return { ...r, cells, fixedDisplayed, totalDisplayed };
    });
    arr.sort((a, b) => b.totalDisplayed - a.totalDisplayed);
    return arr;
  }, [data, withVat, subtractCosts, withForecast, expectedByPlate, expectedPerMonth]);

  // Aggregeret række for bilers HISTORISKE GoMore-udlejnings-måneder i året.
  // Inkluderer:
  //   1) Solgte biler (bevarer historik så omsætningen for fx feb ikke ændrer sig
  //      bare fordi vi solgte bilen i jul.)
  //   2) Carbuddi-biler med historisk GoMore-indtjening — disse hører IKKE til
  //      i den aktive GoMore-flåde, men deres historiske indtjening skal stadig
  //      bidrage til årets samlede omsætning.
  const soldRental = useMemo(() => {
    if (!data?.rows) return null;
    const historic = data.rows.filter((r) => {
      // Solgte biler vises kun hvis de har historisk udlejnings-indtjening i året —
      // biler købt og solgt uden at have været udlejet er ikke relevante her.
      if (r.status === "sold") {
        return (r.actual_months || []).some((v) => v > 0);
      }
      if (r.status === "active" && (r.platform || "gomore") === "carbuddi") {
        return (r.actual_months || []).some((v) => v > 0);
      }
      return false;
    });
    if (historic.length === 0) return null;
    // Summér pr. måned
    const totalGrossByMonth = Array(12).fill(0);
    const totalKmByMonth = Array(12).fill(0);
    historic.forEach((r) => {
      (r.actual_months || []).forEach((v, i) => { totalGrossByMonth[i] += v; });
      (r.months_km || []).forEach((v, i) => { totalKmByMonth[i] += v; });
    });
    const totalGross = totalGrossByMonth.reduce((a, b) => a + b, 0);
    if (totalGross === 0) return null;
    // Byg samme cell-struktur som aktive rækker, så det visuelt smelter ind
    const cells = totalGrossByMonth.map((actualGross) => {
      const baseDisplayed = fromGross(actualGross, withVat);
      const baseEx = fromGross(actualGross, false);
      return {
        actualGross,
        usingExpected: false,
        baseGross: actualGross,
        baseDisplayed,
        baseEx,
        netEx: baseEx,
        netDisplayed: baseDisplayed,
        empty: actualGross === 0,
      };
    });
    const totalDisplayed = cells.reduce((a, c) => a + c.netDisplayed, 0);
    return {
      kind: "sold-rental",
      sold_cars: historic,
      cells,
      totalDisplayed,
      totalKmByMonth,
    };
  }, [data, withVat]);

  // Brugtmoms-ordning: 25/125 = 20% af positiv margin (sale - purchase). Tab er ikke moms-belagt.
  // Aggregeret salgsavance-række (brugtmoms-belagt profit/tab ved salg i året)
  // Brugtmoms-ordning: 25/125 = 20% af positiv margin (sale - purchase). Tab er ikke moms-belagt.
  const salesByMonth = useMemo(() => {
    const arr = Array(12).fill(0);
    if (!data?.rows) return arr;
    for (const r of data.rows) {
      if (r.status === "sold" && r.sale_month_index != null && r.sale_profit != null) {
        const gross = Number(r.sale_profit);
        const brugtmoms = gross > 0 ? gross * 0.20 : 0;
        const net = withVat ? gross : gross - brugtmoms;
        arr[r.sale_month_index] += net;
      }
    }
    return withForecast ? fillForecastAvg(arr) : arr;
  }, [data, withVat, withForecast]);
  const salesTotal = salesByMonth.reduce((a, b) => a + b, 0);

  // Raw arrays (uden forecast) — bruges til at vise prognose-celler med italic styling
  const rawSalesByMonth = useMemo(() => {
    const arr = Array(12).fill(0);
    if (!data?.rows) return arr;
    for (const r of data.rows) {
      if (r.status === "sold" && r.sale_month_index != null && r.sale_profit != null) {
        const gross = Number(r.sale_profit);
        const brugtmoms = gross > 0 ? gross * 0.20 : 0;
        const net = withVat ? gross : gross - brugtmoms;
        arr[r.sale_month_index] += net;
      }
    }
    return arr;
  }, [data, withVat]);
  const rawVariableByMonth = useMemo(() => {
    const arr = data?.general_variable_by_month || Array(12).fill(0);
    return arr.map((v) => (withVat ? v : v / (1 + VAT_RATE)));
  }, [data, withVat]);
  const rawCarbuddiByMonth = useMemo(() => {
    const arr = data?.carbuddi_by_month || Array(12).fill(0);
    return arr.map((v) => (withVat ? v * (1 + VAT_RATE) : v));
  }, [data, withVat]);

  function fillForecastAvg(arr) {
    const nonZero = arr.filter((v) => v > 0);
    if (nonZero.length === 0) return arr;
    const avg = nonZero.reduce((a, b) => a + b, 0) / nonZero.length;
    const firstIdx = arr.findIndex((v) => v > 0);
    return arr.map((v, i) => (i >= firstIdx && v === 0 ? avg : v));
  }

  // Carbuddi displayed: stored EX moms — multiply by 1.25 when withVat ON.
  // Når withForecast er ON: fyld tomme måneder med pr. bil-gennemsnit.
  const carbuddiPerCarForecasted = useMemo(() => {
    const arr = data?.carbuddi_per_car || [];
    return arr.map((cb) => {
      const base = (cb.amounts || []).map((v) => (withVat ? v * (1 + VAT_RATE) : v));
      const amounts = withForecast ? fillForecastAvg(base) : base;
      return { ...cb, amounts };
    });
  }, [data, withVat, withForecast]);

  const carbuddiByMonth = useMemo(() => {
    const arr = Array(12).fill(0);
    carbuddiPerCarForecasted.forEach((cb) => (cb.amounts || []).forEach((v, i) => { arr[i] += v; }));
    return arr;
  }, [carbuddiPerCarForecasted]);
  const carbuddiTotal = carbuddiByMonth.reduce((a, b) => a + b, 0);

  // GoMore (rows) displayed totals pr. måned (uden carbuddi) — ALTID uden pr. bil-omkostninger
  // (subtractCosts påvirker KUN visningen i pr. bil-rækker, ikke aggregeringer)
  const gomoreByMonth = useMemo(() => {
    const arr = Array(12).fill(0);
    rows.forEach((r) => r.cells.forEach((c, i) => { arr[i] += c.baseDisplayed; }));
    if (soldRental) soldRental.cells.forEach((c, i) => { arr[i] += c.baseDisplayed; });
    return arr;
  }, [rows, soldRental]);
  const gomoreTotal = gomoreByMonth.reduce((a, b) => a + b, 0);

  const companyFixedByMonth = useMemo(() => {
    const arr = data?.general_fixed_by_month || Array(12).fill(0);
    // Beløbene er indtastet INKL moms — vis som-er når withVat, ellers ex moms
    return arr.map((v) => withVat ? v : v / (1 + VAT_RATE));
  }, [data, withVat]);
  const companyVariableByMonth = useMemo(() => {
    const arr = data?.general_variable_by_month || Array(12).fill(0);
    const displayed = arr.map((v) => withVat ? v : v / (1 + VAT_RATE));
    return withForecast ? fillForecastAvg(displayed) : displayed;
  }, [data, withVat, withForecast]);

  // Måneder med indtægt (faktisk udlejning, forventet eller bilsalg) — firma-omk trækkes KUN i disse måneder
  const hasIncomeByMonth = useMemo(() => {
    const arr = Array(12).fill(false);
    rows.forEach((r) => {
      r.cells.forEach((c, i) => {
        if (c.baseGross > 0) arr[i] = true;
      });
    });
    // Solgte bilers historiske udlejning tæller altid
    if (soldRental) {
      soldRental.cells.forEach((c, i) => { if (c.baseGross > 0) arr[i] = true; });
    }
    if (withSales) {
      salesByMonth.forEach((v, i) => { if (v !== 0) arr[i] = true; });
    }
    // Carbuddi-udlejning tæller også som indtægt-måneder
    carbuddiByMonth.forEach((v, i) => { if (v > 0) arr[i] = true; });
    return arr;
  }, [rows, soldRental, withSales, salesByMonth, carbuddiByMonth]);

  const effFixedByMonth = useMemo(
    () => companyFixedByMonth.map((v, i) => hasIncomeByMonth[i] ? v : 0),
    [companyFixedByMonth, hasIncomeByMonth]
  );
  const effVariableByMonth = useMemo(
    () => companyVariableByMonth.map((v, i) => hasIncomeByMonth[i] ? v : 0),
    [companyVariableByMonth, hasIncomeByMonth]
  );

  const totalsByMonth = useMemo(() => {
    const t = Array(12).fill(0);
    // TOTAL bruger ALTID base (uden pr. bil-omk) — subtractCosts påvirker kun pr. bil-rækker
    rows.forEach((r) => { r.cells.forEach((c, i) => { t[i] += c.baseDisplayed; }); });
    if (soldRental) {
      soldRental.cells.forEach((c, i) => { t[i] += c.baseDisplayed; });
    }
    // Carbuddi udlejning tæller med i TOTAL
    for (let i = 0; i < 12; i++) t[i] += carbuddiByMonth[i];
    if (withSales) for (let i = 0; i < 12; i++) t[i] += salesByMonth[i];
    if (withCompanyFixed) for (let i = 0; i < 12; i++) t[i] -= effFixedByMonth[i];
    if (withCompanyVariable) for (let i = 0; i < 12; i++) t[i] -= effVariableByMonth[i];
    return t;
  }, [rows, soldRental, withSales, salesByMonth, carbuddiByMonth, withCompanyFixed, withCompanyVariable, effFixedByMonth, effVariableByMonth]);
  const grandTotal = totalsByMonth.reduce((a, b) => a + b, 0);

  // Thresholds-lookup: bil-override > måneds-override > default
  const carThresholdsById = useMemo(() => {
    const map = {};
    (allCars || []).forEach((c) => {
      if (c.revenue_thresholds && c.revenue_thresholds.red != null && c.revenue_thresholds.green != null) {
        map[c.license_plate] = c.revenue_thresholds;
      }
    });
    return map;
  }, [allCars]);

  const getThresholds = (plate, monthIdx) => {
    if (carThresholdsById[plate]) return carThresholdsById[plate];
    const monthKey = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
    const byMonth = settings?.revenue_thresholds_by_month || {};
    if (byMonth[monthKey]) return byMonth[monthKey];
    return settings?.revenue_thresholds || { red: 500, green: 1000 };
  };

  const cellColor = (cell, plate, monthIdx) => {
    if (cell.empty) return "";
    // Brug "reel indtjening" ex moms (netto) hvis omk. trækkes fra, ellers brutto ex moms
    const value = subtractCosts ? cell.netEx : cell.baseEx;
    const t = getThresholds(plate, monthIdx);
    if (value < t.red) return "bg-red-500/15 text-red-400";
    if (value <= t.green) return "bg-amber-500/15 text-amber-400";
    return "bg-emerald-500/15 text-emerald-400";
  };

  const colSpanLeft = subtractCosts ? 4 : 3; // license + bil + total (+omk)

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid="revenue-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500">Omsætning</div>
          <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">Måned for måned.</h1>
          <p className="text-zinc-400 text-sm mt-1">Indtjening pr. bil pr. måned. Vælg scenarier nedenfor.</p>
        </div>
        <div className="flex gap-3 items-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => setUploadOpen(true)}
            className="rounded-none border-zinc-700 h-9 px-3 text-xs"
            data-testid="open-upload-btn"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> GoMore CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setThresholdsOpen(true)}
            className="rounded-none border-zinc-700 h-9 px-3 text-xs"
            data-testid="open-thresholds-btn"
          >
            <SlidersHorizontal className="w-3.5 h-3.5 mr-2" /> Tærskler
          </Button>
          <span className="overline text-zinc-500">År</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-zinc-700 rounded-none px-3 py-2 text-sm"
            data-testid="revenue-year-select"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <RevenueThresholdsDialog
        open={thresholdsOpen}
        onOpenChange={setThresholdsOpen}
        year={year}
        settings={settings}
        cars={allCars}
        onSaved={() => load(year)}
      />

      <GomoreCsvDialog
        key={uploadOpen ? "open" : "closed"}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => { setUploadOpen(false); load(year); }}
      />

      <div className="section-card p-3 sm:p-5 mb-6 grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 sm:gap-x-6 sm:gap-y-4 sticky top-14 lg:top-0 z-30 backdrop-blur-md bg-[#212121]/95" data-testid="revenue-toggles">
        <ToggleRow id="vat" checked={withVat} onChange={setWithVat} title="Med moms" sub={withVat ? "Inkl. 25% moms" : "Ex. moms (÷ 1,25)"} />
        <ToggleRow id="forecast" checked={withForecast} onChange={setWithForecast} title="Forventet indtjening" sub={withForecast ? `Auto pr. bil (snit ${fmt(fromGross(expectedPerMonth, withVat))}/md)` : "Kun faktiske data"} />
        <ToggleRow id="sales" checked={withSales} onChange={setWithSales} title="Inkl. bilsalg" sub={withSales ? "Samlet i én række" : "Vis ikke"} />
        <ToggleRow id="costs" checked={subtractCosts} onChange={setSubtractCosts} title="Træk faste omk. fra (pr. bil)" sub={subtractCosts ? "Reel indtjening" : "Brutto"} />
        <ToggleRow id="company-fixed" checked={withCompanyFixed} onChange={setWithCompanyFixed} title="Faste omk. (firma)" sub={withCompanyFixed ? "Trækkes fra total" : "Vis ikke"} />
        <ToggleRow id="company-variable" checked={withCompanyVariable} onChange={setWithCompanyVariable} title="Variable omk. (firma)" sub={withCompanyVariable ? "Trækkes fra total" : "Vis ikke"} />
      </div>

      {loading && <div className="text-zinc-500 text-sm">Indlæser…</div>}

      {data && (
        <>
          {/* Indtjening i år — flyttet fra Overblik */}
          <SectionTitle>Indtjening i år</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8">
            <DualKPI
              icon={TrendingUp}
              label="GoMore udlejning"
              inclVat={stats ? stats.ytd_revenue : null}
              exVat={stats ? stats.ytd_revenue / 1.25 : null}
              testid="kpi-ytd-revenue"
              accent
            />
            <DualKPI
              icon={Banknote}
              label="Carbuddi udlejning"
              inclVat={stats ? stats.carbuddi_ytd_revenue * 1.25 : null}
              exVat={stats ? stats.carbuddi_ytd_revenue || 0 : null}
              testid="kpi-carbuddi-revenue"
              accent={(stats?.carbuddi_ytd_revenue || 0) > 0}
            />
            <DualKPI
              icon={Tag}
              label="Avance på salg"
              inclVat={stats ? stats.sold_net : null}
              exVat={stats ? (stats.sold_net > 0 ? stats.sold_net * 0.8 : stats.sold_net) : null}
              subLabelEx="efter brugtmoms"
              testid="kpi-sold-net"
              accent={(stats?.sold_net || 0) > 0}
              danger={(stats?.sold_net || 0) < 0}
            />
            <DualKPI
              icon={Gauge}
              label="Samlet"
              inclVat={stats ? (stats.combined_revenue || 0) + (stats.carbuddi_ytd_revenue || 0) * 1.25 : null}
              exVat={stats ? (stats.ytd_revenue / 1.25) + (stats.carbuddi_ytd_revenue || 0) + (stats.sold_net > 0 ? stats.sold_net * 0.8 : stats.sold_net) : null}
              testid="kpi-combined"
            />
          </div>

          <div className="section-card overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="revenue-table">
              <thead className="table-head text-[10px]">
                <tr>
                  <th className="px-2 py-2">Plade</th>
                  <th className="px-2 py-2">Bil</th>
                  {subtractCosts && <th className="text-right px-1.5 py-2 border-r border-zinc-700">Omk.</th>}
                  <th className="text-right px-2 py-2 border-r-2 border-zinc-700">I alt</th>
                  {MONTHS.map((m, i) => (
                    <th key={m} className={"text-right px-1 py-2 " + (i < 11 ? "border-r border-zinc-800/60" : "")}>{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !soldRental && (
                  <tr><td colSpan={colSpanLeft + 12} className="p-8 text-center text-zinc-500">Ingen biler i tabellen.</td></tr>
                )}

                {/* === TOTAL + SAMMENFATTENDE RÆKKER ØVERST === */}
                {rows.length > 0 && (
                  <tr className="border-b-2 border-zinc-700 font-display font-bold bg-[#1A1A1A]" data-testid="row-total-top">
                    <td colSpan={subtractCosts ? 3 : 2} className="px-2 py-2.5 text-[#C8A661] uppercase tracking-wider text-[11px]">Total</td>
                    <td className="text-right tabular-nums px-2 py-2.5 text-gold border-r-2 border-zinc-700">{fmt(grandTotal)}</td>
                    {totalsByMonth.map((v, i) => (
                      <td key={i} className={"text-right tabular-nums px-1 py-2.5 text-[#C8A661] " + (i < 11 ? "border-r border-zinc-800/60" : "")}>{fmt(v)}</td>
                    ))}
                  </tr>
                )}

                {withSales && (
                  <tr className="border-b border-zinc-900 bg-[#1F1F1F]/60" data-testid="row-sales">
                    <td className="text-zinc-300 text-[10px] uppercase tracking-wider px-2 py-2">
                      Bilsalg <span className="text-zinc-500 normal-case tracking-normal">{withVat ? "(brutto)" : "(efter brugtmoms)"}</span>
                    </td>
                    <td className="text-zinc-500 px-2"></td>
                    {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                    <td className={"text-right font-display font-bold tabular-nums px-2 py-2 border-r-2 border-zinc-700 " + (salesTotal > 0 ? "text-emerald-300" : salesTotal < 0 ? "text-red-400" : "")}>
                      {salesTotal === 0 ? "—" : fmtSigned(salesTotal)}
                    </td>
                    {salesByMonth.map((v, i) => {
                      const isForecast = withForecast && rawSalesByMonth[i] === 0 && v !== 0;
                      return (
                        <td
                          key={i}
                          className={
                            "text-right tabular-nums px-1 py-2 " +
                            (i < 11 ? "border-r border-zinc-800/60 " : "") +
                            (v > 0 ? "text-emerald-300 font-semibold" : v < 0 ? "text-red-400 font-semibold" : "text-zinc-700") +
                            (isForecast ? " italic" : "")
                          }
                          title={isForecast ? "Forventet (gns. af måneder med faktiske salg)" : undefined}
                        >
                          {v === 0 ? "—" : fmtSigned(v)}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {/* GoMore + Carbuddi udlejning — samlet række (grøn, ligesom Bilsalg) */}
                {(gomoreTotal + carbuddiTotal) !== 0 && (
                  <tr className="border-b border-zinc-900 bg-[#1F1F1F]/60" data-testid="row-rental-combined">
                    <td className="text-zinc-300 text-[10px] uppercase tracking-wider px-2 py-2">
                      Udlejning <span className="text-zinc-500 normal-case tracking-normal">(GoMore + Carbuddi)</span>
                    </td>
                    <td className="text-zinc-500 px-2"></td>
                    {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                    <td className="text-right font-display font-bold tabular-nums px-2 py-2 border-r-2 border-zinc-700 text-emerald-300">
                      +{fmt(gomoreTotal + carbuddiTotal)}
                    </td>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const v = gomoreByMonth[i] + carbuddiByMonth[i];
                      return (
                        <td
                          key={i}
                          className={
                            "text-right tabular-nums px-1 py-2 " +
                            (i < 11 ? "border-r border-zinc-800/60 " : "") +
                            (v > 0 ? "text-emerald-300 font-semibold" : "text-zinc-700")
                          }
                        >
                          {v > 0 ? `+${fmt(v)}` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {withCompanyFixed && (
                  <tr className="border-b border-zinc-900 bg-[#1F1F1F]/60" data-testid="row-company-fixed">
                    <td className="text-zinc-300 text-[10px] uppercase tracking-wider px-2 py-2">Faste omk. (firma)</td>
                    <td className="px-2"></td>
                    {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                    <td className="text-right font-display font-bold tabular-nums text-red-400/80 px-2 py-2 border-r-2 border-zinc-700">
                      −{fmt(effFixedByMonth.reduce((a, b) => a + b, 0))}
                    </td>
                    {effFixedByMonth.map((v, i) => (
                      <td key={i} className={"text-right tabular-nums text-red-400/80 px-1 py-2 " + (i < 11 ? "border-r border-zinc-800/60" : "")}>
                        {v ? `−${fmt(v)}` : "—"}
                      </td>
                    ))}
                  </tr>
                )}
                {withCompanyVariable && (
                  <tr className="border-b-2 border-zinc-700 bg-[#1F1F1F]/60" data-testid="row-company-variable">
                    <td className="text-zinc-300 text-[10px] uppercase tracking-wider px-2 py-2">Variable omk. (firma)</td>
                    <td className="px-2"></td>
                    {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                    <td className="text-right font-display font-bold tabular-nums text-red-400/80 px-2 py-2 border-r-2 border-zinc-700">
                      −{fmt(effVariableByMonth.reduce((a, b) => a + b, 0))}
                    </td>
                    {effVariableByMonth.map((v, i) => {
                      const isForecast = withForecast && (rawVariableByMonth[i] || 0) === 0 && v > 0;
                      return (
                        <td key={i} className={"text-right tabular-nums text-red-400/80 px-1 py-2 " + (i < 11 ? "border-r border-zinc-800/60" : "") + (isForecast ? " italic" : "")} title={isForecast ? "Forventet (gns. af måneder med faktiske variable omk.)" : undefined}>
                          {v ? `−${fmt(v)}` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {/* === Solgte biler aggregeret række (med udfoldelig dropdown) === */}
                {soldRental && (
                  <>
                    <tr
                      className="border-b border-zinc-900 bg-[#1F1F1F]/40 cursor-pointer hover:bg-[#262626]"
                      data-testid="row-sold-rental"
                      onClick={() => setSoldExpanded((v) => !v)}
                    >
                      <td className="px-2 py-2 text-zinc-300 text-[10px] uppercase tracking-wider">
                        <span className="inline-flex items-center gap-1.5">
                          <ChevronRight className={"w-3 h-3 transition-transform " + (soldExpanded ? "rotate-90" : "")} />
                          Solgte biler · historik
                        </span>
                      </td>
                      <td className="px-2 py-2 text-zinc-500 normal-case tracking-normal text-[10px]">
                        {soldRental.sold_cars.length} bil{soldRental.sold_cars.length === 1 ? "" : "er"}
                      </td>
                      {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                      <td className="text-right font-display font-bold tabular-nums px-2 py-2 border-r-2 border-zinc-700 text-zinc-300">
                        {fmt(soldRental.totalDisplayed)}
                      </td>
                      {soldRental.cells.map((cell, i) => (
                        <td
                          key={i}
                          className={
                            "text-right tabular-nums px-1 py-2 " +
                            (i < 11 ? "border-r border-zinc-800/60 " : "") +
                            (cell.empty ? "text-zinc-700" : "text-zinc-400")
                          }
                        >
                          {cell.empty ? "—" : fmt(cell.netDisplayed)}
                        </td>
                      ))}
                    </tr>
                    {soldExpanded && soldRental.sold_cars.map((sc) => (
                      <tr key={sc.license_plate} className="border-b border-zinc-900 text-[10px]" data-testid={`sold-row-${sc.license_plate}`}>
                        <td className="px-2 py-1.5 pl-6 font-display font-bold tracking-tight text-zinc-300">{sc.license_plate}</td>
                        <td className="text-zinc-500 px-2 py-1.5">
                          {sc.make} {sc.model}
                          {sc.status === "sold" && sc.sale_date && <span className="ml-2 text-zinc-600">· solgt {sc.sale_date.split("-").reverse().join(".")}</span>}
                          {sc.status === "active" && (sc.platform || "gomore") === "carbuddi" && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider border border-[#C8A661]/40 text-[#C8A661]">Nu på Carbuddi</span>
                          )}
                        </td>
                        {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                        <td className="text-right tabular-nums px-2 py-1.5 border-r-2 border-zinc-700 text-zinc-400">
                          {fmt((sc.actual_months || []).reduce((a, v) => a + fromGross(v, withVat), 0))}
                        </td>
                        {(sc.actual_months || []).map((v, i) => (
                          <td key={i} className={"text-right tabular-nums px-1 py-1.5 " + (i < 11 ? "border-r border-zinc-800/60 " : "") + (v > 0 ? "text-zinc-400" : "text-zinc-700")}>
                            {v > 0 ? fmt(fromGross(v, withVat)) : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}

                {/* === GoMore biler (udfoldelig) === */}
                {rows.length > 0 && (
                  <tr
                    className="border-b border-zinc-900 bg-[#1F1F1F]/40 cursor-pointer hover:bg-[#262626]"
                    data-testid="row-gomore-header"
                    onClick={() => setGomoreExpanded((v) => !v)}
                  >
                    <td className="px-2 py-2 text-zinc-300 text-[10px] uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1.5">
                        <ChevronRight className={"w-3 h-3 transition-transform " + (gomoreExpanded ? "rotate-90" : "")} />
                        GoMore biler
                      </span>
                    </td>
                    <td className="px-2 py-2 text-zinc-500 normal-case tracking-normal text-[10px]">
                      {rows.length} bil{rows.length === 1 ? "" : "er"}
                    </td>
                    {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                    <td className="text-right font-display font-bold tabular-nums px-2 py-2 border-r-2 border-zinc-700 text-zinc-300">
                      {fmt(rows.reduce((sum, r) => sum + r.cells.reduce((a, c) => a + c.baseDisplayed, 0), 0))}
                    </td>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const monthTotal = rows.reduce((sum, r) => sum + (r.cells[i].empty ? 0 : r.cells[i].baseDisplayed), 0);
                      return (
                        <td key={i} className={"text-right tabular-nums px-1 py-2 " + (i < 11 ? "border-r border-zinc-800/60 " : "") + (monthTotal > 0 ? "text-zinc-400" : "text-zinc-700")}>
                          {monthTotal > 0 ? fmt(monthTotal) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {gomoreExpanded && rows.map((r) => (
                  <tr key={r.license_plate} className="table-row" data-testid={`revenue-row-${r.license_plate}`}>
                    <td className="font-display font-bold tracking-tight px-2 py-1.5 pl-6">{r.license_plate}</td>
                    <td className="text-zinc-400 px-2 py-1.5">{r.make} {r.model}</td>
                    {subtractCosts && <td className="text-right tabular-nums text-zinc-400 px-1.5 py-1.5 border-r border-zinc-700">{fmt(r.fixedDisplayed)}</td>}
                    <td className="text-right font-display font-bold tabular-nums px-2 py-1.5 border-r-2 border-zinc-700">{fmt(r.totalDisplayed)}</td>
                    {r.cells.map((cell, i) => (
                      <td
                        key={i}
                        className={
                          "text-right tabular-nums px-1 py-1.5 transition-colors " +
                          (i < 11 ? "border-r border-zinc-800/60 " : "") +
                          cellColor(cell, r.license_plate, i) +
                          (cell.empty ? " text-zinc-700" : "") +
                          (cell.usingExpected ? " italic" : "")
                        }
                        data-testid={`cell-${r.license_plate}-${i}`}
                        title={cell.usingExpected ? "Forventet (auto-snit pr. bil)" : cell.actualGross > 0 ? "Faktisk fra GoMore" : "Ingen data"}
                      >
                        {cell.empty ? "—" : fmt(cell.netDisplayed)}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* === Carbuddi samlet (udfoldelig — viser pr. bil ved expand, men IKKE klikbar pr. bil) === */}
                {(data?.carbuddi_total > 0 || carbuddiPerCarForecasted.length > 0) && (
                  <>
                    <tr
                      className="border-b border-zinc-900 bg-[#1F1F1F]/40 cursor-pointer hover:bg-[#262626]"
                      data-testid="row-carbuddi-header"
                      onClick={() => setCarbuddiExpanded((v) => !v)}
                    >
                      <td className="px-2 py-2 text-zinc-300 text-[10px] uppercase tracking-wider">
                        <span className="inline-flex items-center gap-1.5">
                          <ChevronRight className={"w-3 h-3 transition-transform " + (carbuddiExpanded ? "rotate-90" : "")} />
                          Carbuddi · samlet
                        </span>
                      </td>
                      <td className="px-2 py-2 text-zinc-500 normal-case tracking-normal text-[10px]">
                        {carbuddiPerCarForecasted.length} bil{carbuddiPerCarForecasted.length === 1 ? "" : "er"}
                      </td>
                      {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                      <td className="text-right font-display font-bold tabular-nums px-2 py-2 border-r-2 border-zinc-700 text-zinc-300">
                        {fmt(carbuddiTotal)}
                      </td>
                      {carbuddiByMonth.map((v, i) => {
                        const isForecast = withForecast && (rawCarbuddiByMonth[i] || 0) === 0 && v > 0;
                        return (
                          <td key={i} className={"text-right tabular-nums px-1 py-2 " + (i < 11 ? "border-r border-zinc-800/60 " : "") + (v > 0 ? (isForecast ? "text-zinc-500 italic" : "text-zinc-400") : "text-zinc-700")}>
                            {v > 0 ? fmt(v) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                    {carbuddiExpanded && carbuddiPerCarForecasted.map((cb) => {
                      const total = (cb.amounts || []).reduce((a, b) => a + b, 0);
                      // For at vide hvilke celler der er FAKTISKE og hvilke der er PROGNOSE
                      // bruger vi de originale tal (uden forecast) til at sammenligne med.
                      const rawAmounts = (data.carbuddi_per_car || []).find((x) => x.car_id === cb.car_id)?.amounts || [];
                      return (
                        <tr key={cb.car_id} className="border-b border-zinc-900 text-[10px]" data-testid={`carbuddi-row-${cb.license_plate}`}>
                          <td className="px-2 py-1.5 pl-6 font-display font-bold tracking-tight text-zinc-300">{cb.license_plate}</td>
                          <td className="text-zinc-500 px-2 py-1.5">{cb.make} {cb.model}</td>
                          {subtractCosts && <td className="border-r border-zinc-700 px-1.5"></td>}
                          <td className="text-right tabular-nums px-2 py-1.5 border-r-2 border-zinc-700 text-zinc-400">
                            {fmt(total)}
                          </td>
                          {(cb.amounts || []).map((v, i) => {
                            const ym = `${data.year}-${String(i + 1).padStart(2, "0")}`;
                            const isForecast = withForecast && (rawAmounts[i] || 0) === 0 && v > 0;
                            const currentRawAmt = (rawAmounts[i] || 0);
                            return (
                              <td
                                key={i}
                                onDoubleClick={() => setCarbuddiEdit({ car_id: cb.car_id, license_plate: cb.license_plate, make: cb.make, model: cb.model, year_month: ym, current_amount: currentRawAmt })}
                                title="Dobbeltklik for at redigere"
                                className={"text-right tabular-nums px-1 py-1.5 cursor-pointer hover:bg-[#262626] select-none " + (i < 11 ? "border-r border-zinc-800/60 " : "") + (v > 0 ? (isForecast ? "text-zinc-500 italic" : "text-zinc-400") : "text-zinc-700")}
                                data-testid={`carbuddi-cell-${cb.license_plate}-${ym}`}
                              >
                                {v > 0 ? fmt(v) : "—"}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <CarbuddiEditDialog
        target={carbuddiEdit}
        onClose={() => setCarbuddiEdit(null)}
        onSaved={() => { setCarbuddiEdit(null); load(year); }}
      />
    </div>
  );
}


function ToggleRow({ id, checked, onChange, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        id={`toggle-${id}`}
        data-testid={`toggle-${id}`}
        className="data-[state=checked]:bg-[#C8A661]"
      />
      <Label htmlFor={`toggle-${id}`} className="cursor-pointer">
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-zinc-500">{sub}</div>
      </Label>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="overline text-zinc-500 mb-3">{children}</div>;
}

function DualKPI({ icon: Icon, label, inclVat, exVat, subLabelEx, testid, accent, danger }) {
  const valueColor = danger ? "text-red-400" : accent ? "text-[#C8A661]" : "text-white";
  return (
    <div
      className={
        "kpi-card relative p-2 sm:p-4 " +
        (danger ? "border-l-2 border-l-red-500" : accent ? "border-l-2 border-l-[#C8A661]" : "")
      }
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-1 mb-1 sm:mb-2">
        <div className="overline text-[9px] sm:text-xs leading-tight">{label}</div>
        <Icon className={"w-3 h-3 sm:w-4 sm:h-4 shrink-0 " + (danger ? "text-red-400" : accent ? "text-[#C8A661]" : "text-zinc-500")} strokeWidth={2} />
      </div>
      <div className={"kpi-number text-base sm:text-2xl " + valueColor}>
        {inclVat != null ? dkk(inclVat) : "—"}
      </div>
      <div className="text-[8px] sm:text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">inkl. moms</div>
      <div className="border-t border-zinc-800 mt-1 sm:mt-2 pt-1 sm:pt-2">
        <div className="font-display font-bold tracking-tighter text-zinc-300 text-xs sm:text-base">
          {exVat != null ? dkk(exVat) : "—"}
        </div>
        <div className="text-[8px] sm:text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
          ex. moms{subLabelEx ? ` · ${subLabelEx}` : ""}
        </div>
      </div>
    </div>
  );
}


function GomoreCsvDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/transactions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success("CSV importeret");
      onUploaded?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="upload-csv-dialog"
    >
      <div
        className="bg-[#1A1A1A] border border-zinc-700 rounded-none max-w-lg w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white"
          aria-label="Luk"
          data-testid="upload-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-none bg-[#C8A661]/15 text-[#C8A661] flex items-center justify-center shrink-0">
            <FileSpreadsheet size={18} />
          </div>
          <div>
            <div className="overline text-zinc-500 mb-1">Import</div>
            <h2 className="text-xl sm:text-2xl headline">GoMore CSV.</h2>
            <p className="text-xs text-zinc-500 mt-1">.xlsx, .xls eller .csv fra GoMore Mahasa</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <label
            htmlFor="gomore-csv-file"
            className="flex flex-col items-center justify-center px-6 py-10 border-2 border-dashed border-zinc-700 cursor-pointer hover:border-[#C8A661] transition-colors text-center"
          >
            <UploadCloud className="w-8 h-8 text-zinc-500 mb-3" />
            <div className="text-sm text-zinc-300 font-medium">
              {file ? file.name : "Vælg fil eller træk hertil"}
            </div>
            {!file && <div className="text-xs text-zinc-600 mt-1">.xlsx, .xls eller .csv</div>}
            <input
              id="gomore-csv-file"
              type="file"
              accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              data-testid="upload-input"
            />
          </label>

          {result && (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm" data-testid="upload-result">
              <div className="flex items-center gap-2 text-emerald-400 font-medium mb-2">
                <CheckCircle2 size={16} /> Import gennemført
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs text-zinc-300">
                <div>
                  <div className="overline text-zinc-500 mb-0.5">Nye linjer</div>
                  <div className="font-display text-xl font-black text-white">{result.inserted ?? 0}</div>
                </div>
                <div>
                  <div className="overline text-zinc-500 mb-0.5">Opdaterede</div>
                  <div className="font-display text-xl font-black text-white">{result.updated ?? 0}</div>
                </div>
                <div>
                  <div className="overline text-zinc-500 mb-0.5">Nye biler</div>
                  <div className="font-display text-xl font-black text-white">{result.new_cars?.length || 0}</div>
                </div>
              </div>
              {result.new_cars?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <div className="overline text-zinc-500 mb-2">Nye plader oprettet automatisk</div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.new_cars.map((p) => (
                      <span key={p} className="border border-amber-500/40 text-amber-300 text-[10px] px-2 py-1 font-mono">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700">Annullér</Button>
            <Button
              type="submit"
              disabled={!file || busy}
              className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574] disabled:opacity-40"
              data-testid="upload-submit"
            >
              {busy ? "Uploader…" : "Importér"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CarbuddiEditDialog({ target, onClose, onSaved }) {
  const [amountIncl, setAmountIncl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    // current_amount er gemt EX moms — vis det inkl moms i input (samme UX som Cars.jsx)
    const incl = target.current_amount ? target.current_amount * 1.25 : 0;
    setAmountIncl(incl > 0 ? String(Math.round(incl * 100) / 100) : "");
  }, [target]);

  if (!target) return null;

  const amt = parseFloat(amountIncl) || 0;
  const exVat = amt / 1.25;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/carbuddi/earnings", {
        car_id: target.car_id,
        year_month: target.year_month,
        amount: amt,
        vat_included: true,
      });
      toast.success(amt <= 0 ? "Indtjening slettet" : "Indtjening gemt");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1A1A1A] border-zinc-700 max-w-md rounded-none" data-testid="carbuddi-edit-dialog">
        <DialogHeader>
          <div className="overline text-[#C8A661] mb-1">Carbuddi · {target.year_month}</div>
          <DialogTitle className="headline text-2xl">
            {target.license_plate}{" "}
            <span className="text-zinc-400 font-normal text-base">{target.make} {target.model}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Beløb (DKK · inkl. moms)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amountIncl}
              onChange={(e) => setAmountIncl(e.target.value)}
              placeholder="Fx 6 250 (sæt 0 for at slette)"
              className="rounded-none border-zinc-700 mt-2 font-mono text-lg"
              autoFocus
              data-testid="carbuddi-edit-amount"
            />
            {amt > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Ekskl. moms: <strong className="text-zinc-300">{exVat.toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr.</strong> (gemt i regnskab)
              </p>
            )}
            {amt === 0 && target.current_amount > 0 && (
              <p className="text-[11px] text-red-400 mt-1.5">Beløb sat til 0 — registreringen slettes.</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700">Annullér</Button>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574] disabled:opacity-40"
              data-testid="carbuddi-edit-save"
            >
              {busy ? "Gemmer…" : (amt <= 0 ? "Slet" : "Gem")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

